import {yupResolver} from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import {Controller, useForm} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {Form, Popup, List, Container, Icon, Button, Label} from 'semantic-ui-react';
import * as yup from 'yup';

import GroupSearch from 'components/Common/AuthSearch/GroupSearch';
import {useReservedHostnames} from 'hooks';
import {getUserInfo} from 'state/selectors';

import {
  validationSchemas,
  wordpressCategoryOptions,
  wordpressCategoryPurposes,
  wordpressSubdomain,
} from '../Common/sharedElements';

import styles from './WordPressCreation.module.scss';

const formSchema = yup.object().shape({
  category: validationSchemas.categoryValidation,
  name: validationSchemas.drupalNameValidation.test(
    'domainIsUnique',
    'Such domain already exists',
    (value, ctx) => {
      const siteDomain = value + wordpressSubdomain;

      if (ctx.options.context.reservedHostnames.includes(value)) {
        return ctx.createError({message: 'Provided site name is a reserved name'});
      }

      return !ctx.options.context.reservedFullyQualifiedDomainNames.includes(siteDomain);
    }
  ),
  description: validationSchemas.descriptionValidation,
  adminGroup: yup
    .string()
    .required('Administrator group is required')
    .matches(/^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/, 'Administrator group must be valid, e.g. my-group'),
  computingRulesApproved: validationSchemas.computingRulesApproved,
});

const defaultFormValues = {
  category: 'Official',
  name: '',
  adminGroup: '',
  description: '',
  computingRulesApproved: false,
};

const WordPressCreationForm = props => {
  const {
    reservedHostnames,
    reservedFullyQualifiedDomainNames,
    isLoading: isLoadingReservedHostnames,
  } = useReservedHostnames('wordpress');
  const {
    control,
    handleSubmit,
    formState: {isValid, isSubmitting},
  } = useForm({
    context: {reservedHostnames, reservedFullyQualifiedDomainNames},
    mode: 'onChange',
    defaultValues: defaultFormValues,
    resolver: yupResolver(formSchema),
  });

  const userInfo = useSelector(state => getUserInfo(state));

  // Form submission handler, sends a POST request via a function passed from parent component in props.create
  const submitHandler = async data => {
    await props.create({
      project: {
        name: data.name,
        adminEmail: userInfo.email || '',
        description: data.description,
        category: data.category,
        adminGroup: data.adminGroup,
      },
      name: data.name,
    });
  };

  return (
    <Container className={styles.WordPressCreationForm}>
      <a
        href="https://webeos.docs.cern.ch/create_site/#site-category"
        target="_blank"
        rel="noreferrer"
      >
        Our recommendation for choosing a category <Icon name="external alternate" size="small" />
      </a>

      <Form onSubmit={handleSubmit(submitHandler)} loading={isSubmitting}>
        <Controller
          control={control}
          name="category"
          render={({field: {onChange, value, name}, fieldState: {error}}) => (
            <Popup
              trigger={
                <Form.Dropdown
                  onChange={(_evt, data) => onChange(data.value)}
                  name={name}
                  value={value}
                  options={wordpressCategoryOptions}
                  error={error?.message}
                  fluid
                  selection
                  required
                />
              }
              position="left center"
              wide
            >
              <Popup.Header>Specify site's category:</Popup.Header>
              <Popup.Content>
                <List items={wordpressCategoryPurposes} />
                <p>The category can be changed later.</p>
              </Popup.Content>
            </Popup>
          )}
        />

        <Controller
          control={control}
          name="name"
          render={({field: {onChange, value, name}, fieldState: {error}}) => (
            <Popup
              trigger={
                <Form.Input
                  id="siteName"
                  error={error?.message}
                  required
                  onChange={(_evt, data) => onChange(data.value)}
                  name={name}
                  value={value}
                  placeholder="Site name"
                  labelPosition="right"
                  label={{
                    htmlFor: 'siteName',
                    className: styles.VisuallyHidden,
                    children: 'Site name',
                  }}
                >
                  <input />
                  <Label>{wordpressSubdomain}</Label>
                </Form.Input>
              }
              position="left center"
            >
              <Popup.Content>
                Choose descriptive and short name, following naming conventions.
              </Popup.Content>
            </Popup>
          )}
        />

        <Controller
          control={control}
          name="adminGroup"
          render={({field: {onChange, value, name}, fieldState: {error}}) => (
            <Popup
              trigger={
                <GroupSearch
                  id="adminGroup"
                  name={name}
                  value={value}
                  onChange={data => {
                    onChange(data.groupIdentifier);
                  }}
                  error={error?.message}
                  label={{
                    htmlFor: 'adminGroup',
                    className: styles.VisuallyHidden,
                    children: 'Administrator group for the site, e.g. my-group.',
                  }}
                  placeholder="Administrator group for the site, e.g. my-group."
                  required
                />
              }
              position="left center"
            >
              <Popup.Content>
                Specify a CERN e-group to be the administrator group for this site. The members of
                the group will have site management permissions identical to those of site owner,
                and WordPress administrator access in the WordPress site.
                <br />
                <br />
                Please ensure that the website owner is a member of this group for administrative
                access on the Wordpress website
              </Popup.Content>
            </Popup>
          )}
        />

        <Controller
          control={control}
          name="description"
          render={({field: {onChange, value, name}, fieldState: {error}}) => (
            <Popup
              trigger={
                <Form.Input
                  id="siteDescription"
                  label={{
                    htmlFor: 'siteDescription',
                    className: styles.VisuallyHidden,
                    children: 'Site description',
                  }}
                  onChange={(_evt, data) => onChange(data.value)}
                  name={name}
                  value={value}
                  error={error?.message}
                  placeholder="Site description"
                  required
                />
              }
              position="left center"
            >
              <Popup.Content>Description of the site's purpose.</Popup.Content>
            </Popup>
          )}
        />

        <Controller
          control={control}
          name="computingRulesApproved"
          render={({field: {onChange, value, name}}) => (
            <Form.Checkbox
              id="computingRules"
              onChange={(_evt, data) => onChange(data.checked)}
              name={name}
              checked={value}
              label={
                <label htmlFor="computingRules">
                  I have read and agreed to the{' '}
                  <a
                    href="https://security.web.cern.ch/security/rules/en/index.shtml"
                    target="_blank"
                    rel="noreferrer"
                  >
                    CERN Computing Rules
                  </a>{' '}
                  and taken into account the{' '}
                  <a
                    href="http://design-guidelines.web.cern.ch/guidelines-cern-websites"
                    target="_blank"
                    rel="noreferrer"
                  >
                    design guidelines
                  </a>{' '}
                  for websites and the{' '}
                  <a href="https://webeos.docs.cern.ch/lifecycle/" target="_blank" rel="noreferrer">
                    website lifecycle policy
                  </a>
                  .
                </label>
              }
            />
          )}
        />

        <Button
          type="submit"
          className={styles.SaveButton}
          floated="right"
          disabled={!isValid || isLoadingReservedHostnames || isSubmitting}
        >
          Create
        </Button>
      </Form>
    </Container>
  );
};

WordPressCreationForm.propTypes = {
  create: PropTypes.func.isRequired,
};

export default WordPressCreationForm;
